import { Component, OnInit, AfterViewInit } from '@angular/core';
import { ROUTES } from 'src/app/constants/Routes';

declare var AOS: any;

@Component({
  selector: 'app-certificate-management-system',
  templateUrl: './certificate-management-system.component.html',
  styleUrls: ['./certificate-management-system.component.scss'],
  standalone: false
})
export class CertificateManagementSystemComponent implements OnInit, AfterViewInit {
  Routes = ROUTES;
  activeSection = 'overview';

  sections = [
    { id: 'overview', label: 'Overview', icon: 'bx-layer' },
    { id: 'capabilities', label: 'Core Capabilities', icon: 'bx-shield-quarter' },
    { id: 'lifecycle', label: 'Certificate Lifecycle', icon: 'bx-refresh' },
    { id: 'usecases', label: 'Use Cases', icon: 'bx-briefcase' },
  ];

  pillars = [
    {
      icon: 'bx-shield-quarter',
      title: 'PKI-Driven Trust Architecture',
      points: [
        'Root CA & Intermediate CA hierarchy',
        'Device identity provisioning',
        'Hardware-backed certificate storage',
        'Mutual TLS (mTLS) authentication',
        'Digital signing & verification',
        'Secure key lifecycle management',
      ]
    },
    {
      icon: 'bx-refresh',
      title: 'Certificate Lifecycle Management',
      points: [
        'Certificate issuance & automated provisioning',
        'Scheduled rotation & renewal',
        'Revocation handling via CRL/OCSP',
        'Fleet-wide certificate orchestration',
        'Policy-driven expiry notifications',
      ]
    },
    {
      icon: 'bx-lock-alt',
      title: 'Zero-Trust Security Model',
      points: [
        'Verify every device, service, and API',
        'Continuous re-authentication at runtime',
        'Encrypted communication channels',
        'Policy-driven, least-privilege access control',
        'Immutable audit trails for forensics',
      ]
    },
  ];

  capabilities = [
    {
      icon: 'bx-chip',
      title: 'Device Identity Management',
      desc: 'Unique cryptographic digital identity provisioned for every connected device, ECU, and gateway at factory or field stage.'
    },
    {
      icon: 'bx-server',
      title: 'PKI Infrastructure',
      desc: 'Root & Intermediate CA management with hierarchical trust chains, supporting multi-OEM and multi-tenant deployments.'
    },
    {
      icon: 'bx-certification',
      title: 'Secure Certificate Provisioning',
      desc: 'High-throughput factory-line and field provisioning with support for both online and offline workflows.'
    },
    {
      icon: 'bx-link-alt',
      title: 'Mutual Authentication (mTLS)',
      desc: 'Device-to-cloud and device-to-device trust establishment using certificate-based mutual TLS handshakes.'
    },
    {
      icon: 'bx-cloud-upload',
      title: 'Secure OTA Signing',
      desc: 'Cryptographically signed firmware and software package delivery with integrity validation at the endpoint.'
    },
    {
      icon: 'bx-refresh',
      title: 'Certificate Rotation & Revocation',
      desc: 'Automated lifecycle management with CRL and OCSP validation to isolate compromised devices instantly.'
    },
    {
      icon: 'bx-file-find',
      title: 'Security Audit Trails',
      desc: 'Tamper-evident logging for compliance, incident response, and forensic readiness across all operations.'
    },
    {
      icon: 'bx-buildings',
      title: 'Multi-Tenant Architecture',
      desc: 'Strict OEM and ecosystem isolation with per-tenant PKI hierarchies and policy engines.'
    },
  ];

  lifecycleStages = [
    'Certificate issuance & automated provisioning',
    'Scheduled rotation & renewal',
    'Revocation handling via CRL/OCSP',
    'Fleet-wide certificate orchestration',
    'Policy-driven expiry notifications',
    'Tamper-evident audit trails throughout',
  ];

  useCases = [
    {
      title: 'Connected Vehicles',
      icon: 'bx-car',
      points: [
        'TCU authentication & secure telematics',
        'ECU trust management & access control',
        'Encrypted V2X communication channels',
      ]
    },
    {
      title: 'OTA Security',
      icon: 'bx-cloud-upload',
      points: [
        'Cryptographic firmware signing',
        'Package integrity validation pre-install',
        'Secure, authenticated software deployment',
      ]
    },
    {
      title: 'Industrial IoT',
      icon: 'bx-network-chart',
      points: [
        'Device authentication at the edge',
        'Secure factory connectivity',
        'Edge-to-cloud data protection',
      ]
    },
    {
      title: 'Smart Mobility',
      icon: 'bx-broadcast',
      points: [
        'V2X trust establishment',
        'Fleet-wide certificate management',
        'Connected infrastructure authentication',
      ]
    },
  ];

  highlights = [
    'Hardware-backed keys with HSM or Secure Element support.',
    'Automated certificate issuance, rotation, and revocation.',
    'TLS everywhere with signed firmware and OTA packages.',
    'RBAC, audit logs, and policy-driven controls at enterprise scale.',
  ];

  ngOnInit() {}

  ngAfterViewInit() {
    try { AOS.refresh(); } catch (e) {}
  }

  scrollTo(id: string) {
    this.activeSection = id;
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
}